import { useState } from "react";
import dayjs from "dayjs";
import { ClassHomeWorkRow } from "components/molecules/ClassHomeWorkRow";
import { DrillModal } from "./DrillModal";
import { DrillSmartModal } from "components/molecules/DrillSmartModal";
import "./index.scss";
import { useSegmentReFetch } from "context";
import {
  homeApi,
  useGetCourseworkQuery,
  useGetOverallProgressQuery,
  useGetTestScoresAndPerformanceQuery,
} from "services/homeApi";
import { getObjectKeysByValue } from "services/helpers";
import { getTimeFilterInput } from "utils/functions/getTimeFilterInput";
import {
  reStructureSegment,
  replaceArrayOfObjects,
} from "utils/functions/courseWorkReStructure";
import { formatToLocalDate } from "utils/functions/formatToLocalDate";
import { decodeToken } from "utils/functions/decodeToken";
import { useLazyGetStudyPlannerEventsQuery } from "services/studyPlanner";
import { useLazyGetAiRecommendationsQuery } from "services/aiRecommendations";
import { DrillPaginationTabDashboard } from "./DrillPaginationTabDashboard";
import { useLazyGetCourseIndexQuery } from "services/courseIndex";
import { RefreshModal } from "components/molecules";
import { useAppDispatch } from "hooks";
import { MCAT_TEST_TYPE } from "utils/constants";
 
import { openExternalUrl } from "utils/functions/openExternalUrl";
import { getDrillSmartOnStartDrill } from "utils/functions/getDrillSmartOnStartDrill";
import { useDrillSmart } from "hooks/useDrillSmart";
import { getRowDisplayForWork, type DrillSmartModalData } from "utils/functions/drillSmart";

interface IProps {
  isBtn?: boolean;
  isSecondBtn?: boolean;
  secondBtnLabel?: string;
  isMobile: boolean;
  works?: any;
  isCourseIndex?: boolean;
  type?: string;
  userGuid?: string;
  contentObjectGuid?: string;
  contentObjectType?: string;
  theme: "light" | "dark";
  itemPageNo?: number;
}

export const TabClassHomeWork = ({
  isSecondBtn,
  secondBtnLabel,
  isBtn,
  isMobile,
  works,
  isCourseIndex,
  type,
  userGuid,
  contentObjectGuid,
  contentObjectType,
  theme,
  itemPageNo,
}: IProps) => {
  const [openUpdateModalState, setOpenUpdateModalState] = useState<any>({
    isOpen: false,
    data: null,
  });

  const [isRefreshModalOpen, setIsRefreshModalOpen] = useState({
    isOpen: false,
    data: "",
  });

  const drillSmart = useDrillSmart();

  const { data, setData, selectedBtn, selectedSession } = useSegmentReFetch();

  const { refetch: refetchTestScores, isFetching: isFetchingTestScores } =
    useGetTestScoresAndPerformanceQuery();

  const payload = decodeToken();

  const dispatch = useAppDispatch();

  const userId = payload ? payload.userid : null;
  const productCode = payload ? payload.productcode : null;
  const offeringCode = payload ? payload.offeringcode : null;

  const testtype = payload &&
    payload.hasOwnProperty("testtype") && payload.testtype;

  const isMCAT = testtype === MCAT_TEST_TYPE;

  const {
    refetch: refetchOverAllProgress,
    isFetching: isFetchingOverallProgress,
  } = useGetOverallProgressQuery({
    Userid: userId,
    Productcode: productCode,
    Offeringcode: offeringCode,
  });

  const [ triggerRefetchStudyPlannerEvents, studyPlannerEventsRefetchResult ] =
    useLazyGetStudyPlannerEventsQuery();

  const [ triggerCourseIndexRefetch, refetchCourseIndexIsResult ] =
    useLazyGetCourseIndexQuery();

  const { refetch, isFetching: isFetchingCoursework } = useGetCourseworkQuery({
    page: itemPageNo ? itemPageNo : 1,
    selectedOption: parseInt(getObjectKeysByValue(selectedBtn, true)[0], 10),
    timeFilter:
      selectedSession === null
        ? 1
        : getTimeFilterInput(Object.values(selectedSession)[0]),
  });

   const [
    triggerRefetchAiRecommendation,
    aiRecommendationRefetchResult,
  ] = useLazyGetAiRecommendationsQuery();

  const handleRefreshCourseWork = async () => {
    const promises = [
      refetch(),
      triggerCourseIndexRefetch(),
      refetchTestScores(),
      refetchOverAllProgress(),
      triggerRefetchStudyPlannerEvents(),
      triggerRefetchAiRecommendation(),
    ];

    Promise.allSettled(promises).then((responses: any) => {
      handleRefreshModalClose();
      let updatedArray: any[] | undefined;
      if (responses[0].status === "fulfilled") {
        try {
          const deStructuredArray = reStructureSegment(responses[0].value.data);
          updatedArray = replaceArrayOfObjects(deStructuredArray, data);
          if (updatedArray) {
            setData([...updatedArray]);
          }
        } catch (_) {
          // keep modal closed; data update failed
        }
      }

      drillSmart?.reSyncModalData((groupName) => {
        if (!updatedArray || !groupName) return null;
        for (const segment of updatedArray) {
          const categories = segment.Categories || {};
          for (const key of Object.keys(categories)) {
            const category = categories[key];
            const drillGroup = (category.DrillSmartContent || []).find(
              (ds: any) => ds.GroupName === groupName
            );
            if (drillGroup) {
              return {
                content: drillGroup.Content,
                drillSmartResult: drillGroup.DrillSmartResult,
                drillSmartLaunchUrl: drillGroup.LaunchUrl,
                groupName: drillGroup.GroupName,
                nextDrill: drillGroup.NextDrill,
              };
            }
          }
        }
        return null;
      });
    });
    dispatch(homeApi.util.invalidateTags(["Coursework"]));
  };

  const handlePrimaryButtonClick = (work: any, type?: string) => {
    if (drillSmart?.isDrillSmartGrouping(work)) {
      drillSmart?.openModal(work);
      return;
    }
    if (
      work.ContentObjectType === "OfflineDrill" ||
      work.ContentObjectType === "OfflineReading"
    ) {
      setOpenUpdateModalState((prev: any) => ({
        ...prev,
        isOpen: true,
        data: work,
      }));
    } else {
      openExternalUrl({
        url: work.LaunchUrl,
        contentObjectType: work.ContentObjectType,
        testType: testtype,
        dispatch,
        onRefreshModalOpen: (contentType) => {
          setIsRefreshModalOpen((prev) => ({
            ...prev,
            isOpen: true,
            data: contentType || "Test",
          }));
        },
      });
    }
  };

  const handleSecondaryButtonClick = (work: any) => {
    if (work.ScoreReportUrl !== null) {
      openExternalUrl({
        url: work.ScoreReportUrl,
        title: "Score Report",
        testType: testtype,
        dispatch,
      });
    }
  };

  const handleUpdateModalClose = () => {
    setOpenUpdateModalState((prev: any) => ({
      ...prev,
      isOpen: false,
      data: null,
    }));
  };

  const handleRefreshModalClose = () => {
    setIsRefreshModalOpen((prev) => ({
      ...prev,
      isOpen: false,
      data: "",
    }));
  };

  const rawData = drillSmart?.modalState?.data;
  const drillSmartModalData: DrillSmartModalData | null =
    rawData != null && rawData !== "" ? (rawData as DrillSmartModalData) : null;

  return (
    <>
      {works && Array.isArray(works) && works.length > 5 ? (
        <DrillPaginationTabDashboard
          exams={works}
          theme={theme}
          type={type}
          handlePrimaryBtnClick={(work: any) => handlePrimaryButtonClick(work, type)}
          handleSecondaryBtnClick={handleSecondaryButtonClick}
        />
      ) : (
        works &&
        Array.isArray(works) &&
        works.map((work: any, index: number) => {
          const rowDisplay = getRowDisplayForWork(work, {
            formatDateCompleted: (d) => formatToLocalDate(d, "MM/DD/YYYY"),
          });
          return (
            <div className="class-home-container" key={index}>
              <ClassHomeWorkRow
                index={index}
                textIconLabel={rowDisplay?.textIconLabel ?? ""}
                iconType={rowDisplay?.iconType ?? "empty-circle"}
                middleLabel={rowDisplay?.middleLabel ?? ""}
                isMobile={isMobile}
                handlePrimaryBtnClick={() => handlePrimaryButtonClick(work, type)}
                handleSecondaryBtnClick={() =>
                  handleSecondaryButtonClick(work)
                }
                rowId={`${type}-home-tab-row-${index}`}
                theme={theme}
                contentObjectType={rowDisplay?.contentObjectType ?? ""}
                type={type}
                itemPageNo={itemPageNo}
                status={rowDisplay?.status ?? "Not Started"}
                drillSmartResult={rowDisplay?.drillSmartResult}
                badge={rowDisplay?.badge ?? ""}
              />
            </div>
          );
        })
      )}

      <DrillSmartModal
        handleOnClose={drillSmart?.closeModal ?? (() => {})}
        isOpen={drillSmart?.modalState?.isOpen ?? false}
        theme={theme}
        data={drillSmartModalData}
        onStartDrill={getDrillSmartOnStartDrill(
          drillSmartModalData,
          (contentType) =>
            setIsRefreshModalOpen((prev) => ({ ...prev, isOpen: true, data: contentType || "Drill" })),
          openExternalUrl,
          dispatch,
          testtype
        )}
      />

      <DrillModal
        handleUpdateModalClose={handleUpdateModalClose}
        openUpdateModalState={openUpdateModalState}
        theme={theme}
        refetchData={handleRefreshCourseWork}
        isFetchingOtherAPIs={
          isFetchingCoursework ||
          isFetchingOverallProgress ||
          isFetchingTestScores ||
          refetchCourseIndexIsResult?.isFetching ||
          studyPlannerEventsRefetchResult.isFetching ||
          aiRecommendationRefetchResult.isFetching
        }
      />

      <RefreshModal
        isOpen={isRefreshModalOpen.isOpen}
        handleOnClose={handleRefreshModalClose}
        theme={theme}
        contentObjectType={
          isRefreshModalOpen.data !== ""
            ? isRefreshModalOpen.data
            : contentObjectType
        }
        isFetching={
          isFetchingCoursework ||
          isFetchingOverallProgress ||
          isFetchingTestScores ||
          refetchCourseIndexIsResult?.isFetching ||
          studyPlannerEventsRefetchResult.isFetching ||
          aiRecommendationRefetchResult.isFetching
        }
        handleRefresh={handleRefreshCourseWork}
      />
    </>
  );
};

export { DrillModal };
